import { AlertTriangle, Info, X, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from './cn'
import { t } from '@/lib/i18n'

export type BannerTone = 'info' | 'warning' | 'danger'

export interface BannerProps {
  tone?: BannerTone
  children: ReactNode
  /** Replace the default tone icon (pass null to hide it). */
  icon?: ReactNode
  /** Optional action, usually a small Button. */
  action?: ReactNode
  /** Shows an X button when provided. */
  onDismiss?: () => void
  className?: string
}

const tones: Record<BannerTone, { box: string; icon: string; Icon: typeof Info }> = {
  info: { box: 'bg-accent-tint border-accent/25', icon: 'text-accent-strong', Icon: Info },
  warning: { box: 'bg-warning-tint border-warning/25', icon: 'text-warning', Icon: AlertTriangle },
  danger: { box: 'bg-danger-tint border-danger/25', icon: 'text-danger', Icon: XCircle },
}

/** Full width strip under the tabs for account and community notices. */
export function Banner({ tone = 'info', children, icon, action, onDismiss, className }: BannerProps) {
  const tn = tones[tone]
  return (
    <div
      role={tone === 'info' ? 'status' : 'alert'}
      className={cn('flex w-full items-center gap-3 border-y px-4 py-2.5 text-body text-text', tn.box, className)}
    >
      {icon !== null && (
        <span className={cn('flex shrink-0 [&_svg]:size-[18px]', tn.icon)} aria-hidden="true">
          {icon ?? <tn.Icon strokeWidth={2} />}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('Dismiss')}
          className="-mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-black/5 hover:text-text focus-ring dark:hover:bg-white/10"
        >
          <X className="size-4" strokeWidth={1.75} />
        </button>
      )}
    </div>
  )
}
